import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPostBySlug, getBlogPosts } from '../services/blogService';
import type { BlogPost } from '../types';
import DOMPurify from 'dompurify';
import { linkifyHtml } from '../utils/linkify';
import { marked } from 'marked';
import { updateMetaTag, updateLinkTag } from '../hooks/useSeo';
import { AWIN_AFFILIATE_URL_PREFIX, NAMECHEAP_SEARCH_URL_PREFIX } from '../constants';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [customDomainQuery, setCustomDomainQuery] = useState('');

  const createAffiliateUrl = (brandName: string) => {
    const cleanQuery = (brandName || 'brandgenius').trim();
    const namecheapUrl = `${NAMECHEAP_SEARCH_URL_PREFIX}${cleanQuery}`;
    return `${AWIN_AFFILIATE_URL_PREFIX}${encodeURIComponent(namecheapUrl)}`;
  };

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      if (slug) {
        const fetchedPost = await getBlogPostBySlug(slug);
        setPost(fetchedPost);

        if (fetchedPost) {
          const baseTopic = fetchedPost.slug
            .replace(/-brand-name-ideas$/, '')
            .replace(/-domain-name-ideas$/, '');
          setCustomDomainQuery(baseTopic.replace(/-/g, ''));

          const allPosts = await getBlogPosts(true);
          const companionSlug = fetchedPost.slug.endsWith('-brand-name-ideas')
            ? `${baseTopic}-domain-name-ideas`
            : `${baseTopic}-brand-name-ideas`;

          const companion = allPosts.find(p => p.slug === companionSlug);
          const others = allPosts.filter(
            p =>
              p.id !== fetchedPost.id &&
              p.slug !== companionSlug &&
              (p.categories.some(c => fetchedPost.categories.includes(c)) ||
                p.tags.some(t => fetchedPost.tags.includes(t)))
          );

          const combinedRelated = companion ? [companion, ...others].slice(0, 6) : others.slice(0, 6);
          setRelatedPosts(combinedRelated);
        }
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('app-rendered'));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, post]);

  useIsomorphicLayoutEffect(() => {
    if (post) {
      document.title = post.seoTitle || post.title;
      updateMetaTag('name', 'description', post.seoDescription || post.excerpt);
      updateMetaTag('property', 'og:title', post.seoTitle || post.title);
      updateMetaTag('property', 'og:description', post.seoDescription || post.excerpt);
      updateMetaTag('property', 'og:type', 'article');
      if (post.featuredImage) {
        updateMetaTag('property', 'og:image', post.featuredImage);
      }
      updateLinkTag('canonical', `https://www.brandgenius.app/blog/${post.slug}/`);
    }
  }, [post]);

  if (loading) return <div className="text-center py-20">Loading post...</div>;
  if (!post) return <div className="text-center py-20">Post not found.</div>;

  const normalizedRawContent = (post.content || '').replace(/^[ \t]+(?=<\/?[a-zA-Z])/gm, '');
  const isStructuredHtml = /^\s*<(p|div|h[1-6]|section|article|table|ul|ol)\b/i.test(normalizedRawContent);

  let contentHtml = normalizedRawContent;
  if (!isStructuredHtml) {
    try {
      contentHtml = marked.parse(normalizedRawContent) as string;
    } catch (e) {
      // ignore
    }
  }

  const sanitizedContent = linkifyHtml(
    DOMPurify.sanitize(contentHtml, { ADD_ATTR: ['class', 'target', 'rel'] })
  );

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.seoTitle || post.title,
    description: post.seoDescription || post.excerpt,
    image: post.featuredImage ? [post.featuredImage] : undefined,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: {
      '@type': 'Organization',
      name: post.author || 'BrandGenius',
      url: 'https://www.brandgenius.app/'
    },
    publisher: {
      '@type': 'Organization',
      name: 'BrandGenius',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.brandgenius.app/brand-name-generator-logo.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.brandgenius.app/blog/${post.slug}/`
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <article className="w-full max-w-4xl mx-auto py-12 md:py-20 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
        <nav className="mb-6 text-sm text-gray-500 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-indigo-600">Home</Link>
          <span>/</span>
          <Link to="/blog/" className="hover:text-indigo-600">Blog</Link>
          <span>/</span>
          <span className="text-gray-800 font-medium truncate max-w-xs sm:max-w-md">{post.title}</span>
        </nav>

        <header className="mb-10 text-center">
          <div className="flex justify-center gap-2 mb-4 flex-wrap">
            {post.categories.map(cat => (
              <Link
                key={cat}
                to={`/blog/category/${encodeURIComponent(cat)}/`}
                className="text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md transition-colors"
              >
                {cat}
              </Link>
            ))}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center justify-center text-gray-500 text-sm gap-4">
            <span>By <span className="font-medium text-gray-700">{post.author}</span></span>
            <span>&bull;</span>
            <time dateTime={post.publishedAt}>{new Date(post.publishedAt).toLocaleDateString()}</time>
          </div>
        </header>

        {post.featuredImage && (
          <div className="mb-10 rounded-2xl overflow-hidden shadow-md">
            <img src={post.featuredImage} alt={post.title} className="w-full h-auto max-h-[460px] object-cover" />
          </div>
        )}

        {/* Interactive Check Domain Name Availability Tool Box (Same Affiliate Flow as Generator & Brand Audit Reports) */}
        <div className="mb-12 p-6 md:p-8 bg-white rounded-2xl border-2 border-emerald-500/30 shadow-md">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <span className="inline-block px-2.5 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider rounded mb-1">
                Instant Domain Lookup
              </span>
              <h2 className="text-xl font-extrabold text-gray-900">
                Check Domain Name Availability
              </h2>
              <p className="text-sm text-gray-600">
                Test any brand name or domain idea from this guide to verify live .com, .co, .shop &amp; .io availability on Namecheap.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={customDomainQuery}
              onChange={e => setCustomDomainQuery(e.target.value)}
              placeholder="Enter a brand or domain name idea (e.g. Solvane)"
              className="flex-grow px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-gray-900 font-medium"
            />
            <a
              href={createAffiliateUrl(customDomainQuery || 'brandgenius')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap"
            >
              Check {customDomainQuery ? `${customDomainQuery.replace(/\.[a-z]+$/i, '')}.com` : 'Domain'} Availability
            </a>
          </div>
        </div>

        <div
          className="prose prose-lg max-w-none prose-indigo mx-auto text-gray-700 mb-16"
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />

        {post.tags.length > 0 && (
          <div className="border-t border-gray-100 pt-8 mb-12">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Target Topics &amp; Tags:</h3>
            <div className="flex gap-2 flex-wrap">
              {post.tags.map(tag => (
                <Link
                  key={tag}
                  to={`/blog/tag/${encodeURIComponent(tag)}/`}
                  className="text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-md transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Check Domain Name Availability + Core Tools CTA */}
        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-8 mb-16 text-center">
          <h3 className="text-2xl font-bold text-indigo-900 mb-3">Ready to Launch &amp; Register Your Brand?</h3>
          <p className="text-indigo-700 mb-6 max-w-2xl mx-auto">
            Check live domain registration availability, generate custom algorithmic brand names, or run a deterministic 5-pillar consultant brand audit.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 flex-wrap">
            <a
              href={createAffiliateUrl(customDomainQuery || post.slug.replace(/-brand-name-ideas|-domain-name-ideas/g, ''))}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              Check Domain Name Availability
            </a>
            <Link
              to="/"
              className="w-full sm:w-auto px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-md transition-all"
            >
              Brand Name Generator
            </Link>
            <Link
              to="/brand-audit/"
              className="w-full sm:w-auto px-6 py-3.5 bg-white hover:bg-gray-50 text-indigo-700 border border-indigo-200 font-bold rounded-xl shadow-sm transition-all"
            >
              Free Brand Audit
            </Link>
          </div>
        </div>

        {relatedPosts.length > 0 && (
          <div className="border-t border-gray-100 pt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Related Brand &amp; Domain Name Guides</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map(related => (
                <div
                  key={related.id}
                  className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-md transition-shadow flex flex-col"
                >
                  {related.featuredImage && (
                    <Link to={`/blog/${related.slug}/`} className="block mb-4 overflow-hidden rounded-lg">
                      <img
                        src={related.featuredImage}
                        alt={related.title}
                        className="w-full h-32 object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </Link>
                  )}
                  <div className="flex gap-1.5 mb-2 flex-wrap">
                    {related.categories.slice(0, 2).map(c => (
                      <span key={c} className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                        {c}
                      </span>
                    ))}
                  </div>
                  <Link to={`/blog/${related.slug}/`} className="block mb-2">
                    <h4 className="font-bold text-gray-900 hover:text-indigo-600 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                  </Link>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-auto">{related.excerpt}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
};
